import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Send, Loader2 } from "lucide-react";

const USAGE_OPTIONS = [
    { value: "eigennutzung", label: "Eigennutzung" },
    { value: "kapitalanlage", label: "Kapitalanlage" },
    { value: "unentschieden", label: "Noch unentschieden" },
];

const FINANCING_OPTIONS = [
    { value: "eigenkapital", label: "Eigenkapital vorhanden" },
    { value: "finanzierung_benoetigt", label: "Finanzierung benötigt" },
    { value: "bereits_finanziert", label: "Finanzierung bereits geklärt" },
    { value: "unentschieden", label: "Noch unentschieden" },
];

export const InquiryForm = ({ apartment, onSuccess }) => {
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        usage_type: "eigennutzung",
        financing: "unentschieden",
        move_in_timing: "",
        message: "",
        privacy_consent: false,
    });

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.privacy_consent) {
            toast.error("Bitte stimmen Sie den Datenschutzhinweisen zu.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                ...form,
                apartment_id: apartment.id,
                apartment_label: apartment.name,
            });
            toast.success("Anfrage gesendet", {
                description: "Wir melden uns zeitnah bei Ihnen mit weiteren Details.",
            });
            setForm({
                name: "",
                email: "",
                phone: "",
                usage_type: "eigennutzung",
                financing: "unentschieden",
                move_in_timing: "",
                message: "",
                privacy_consent: false,
            });
            onSuccess?.();
        } catch (err) {
            toast.error("Anfrage konnte nicht gesendet werden", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
            <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Name *">
                    <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        className="input-line"
                        placeholder="Vor- und Nachname"
                        data-testid="inquiry-name"
                    />
                </Field>
                <Field label="E-Mail *">
                    <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className="input-line"
                        placeholder="ihre@email.de"
                        data-testid="inquiry-email"
                    />
                </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Telefon">
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className="input-line"
                        placeholder="+49 …"
                        data-testid="inquiry-phone"
                    />
                </Field>
                <Field label="Wunsch-Einzug">
                    <input
                        type="text"
                        value={form.move_in_timing}
                        onChange={(e) => update("move_in_timing", e.target.value)}
                        className="input-line"
                        placeholder="z. B. Sommer 2026"
                        data-testid="inquiry-move-in"
                    />
                </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Verwendung">
                    <select
                        value={form.usage_type}
                        onChange={(e) => update("usage_type", e.target.value)}
                        className="input-line"
                        data-testid="inquiry-usage"
                    >
                        {USAGE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Finanzierung">
                    <select
                        value={form.financing}
                        onChange={(e) => update("financing", e.target.value)}
                        className="input-line"
                        data-testid="inquiry-financing"
                    >
                        {FINANCING_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </Field>
            </div>

            <Field label="Ihre Nachricht">
                <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="input-line resize-none"
                    placeholder="Fragen, Wünsche, Termin­vorschläge …"
                    data-testid="inquiry-message"
                />
            </Field>

            <label className="flex items-start gap-3 cursor-pointer group">
                <input
                    type="checkbox"
                    checked={form.privacy_consent}
                    onChange={(e) => update("privacy_consent", e.target.checked)}
                    className="mt-1 h-4 w-4 rounded-none border-stone-300 text-brand-500 focus:ring-brand-500"
                    data-testid="inquiry-consent"
                    required
                />
                <span className="font-body text-xs text-stone-600 leading-relaxed">
                    Ich willige ein, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert
                    und vom Bauträger genutzt werden. Weitere Informationen in der{" "}
                    <a href="/datenschutz" className="underline hover:text-brand-600">
                        Datenschutzerklärung
                    </a>
                    . Ich kann meine Einwilligung jederzeit widerrufen.
                </span>
            </label>

            <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="inquiry-submit"
            >
                {submitting ? (
                    <>
                        <Loader2 size={16} className="animate-spin" />
                        Wird gesendet…
                    </>
                ) : (
                    <>
                        <Send size={16} />
                        Anfrage absenden
                    </>
                )}
            </button>
        </form>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label className="label-line">{label}</label>
        {children}
    </div>
);
